import { UserApi } from '@/apis/user';
import { EApiBaseCode } from '@/constants';
import { useUserInfoStore } from '@/store/user/slice';
import { syncArmsConfig } from '@/utils/sync-arms-config';
import { registerTrackerParams } from '@/utils/tracker';

import {
    getProfileHydratedOk,
    getProfileHydrationInflight,
    setProfileHydratedOk,
    setProfileHydrationInflight,
} from './user-profile-hydration-state';

import type { IUserProfileData } from '@/apis/user/typings';
import type { IUserProfile } from '@/typings/user';

export function mapUserProfileData(data: IUserProfileData): Partial<IUserProfile> {
    return {
        user_id: data.user_id,
        name: data.nick_name,
        avatar: data.avatar,
        login_source: data.login_source,
        email: data.email,
        create_time: data.create_time,
        auto_unlock_status: data.auto_unlock_status ?? false,
        is_new_user: data.is_new_user ?? false,
        // 新老用户判定只依赖接口下发（用户时区当日注册口径），前端不按时间戳自行推导
        is_same_day_register: data.is_same_day_register ?? false,
    };
}

/**
 * 拉取 /user/profile/v1 并写入 store（依赖 withCredentials 携带 cookie）。
 * - 与 runSessionBootstrap 并行调用时，合并共享同一 in-flight 请求。
 * - 成功后同一会话内再次调用会直接返回 true，不再发请求。
 */
export async function hydrateUserProfileFromApi(): Promise<boolean> {
    if (getProfileHydratedOk()) {
        return true;
    }
    const existing = getProfileHydrationInflight();
    if (existing) {
        return existing;
    }

    const promise = (async () => {
        try {
            const res = await UserApi.getUserProfile();
            if (res.code !== EApiBaseCode.SUCCESS || !res.data) {
                return false;
            }
            useUserInfoStore.getState().updateUserInfo(mapUserProfileData(res.data));
            useUserInfoStore.getState().setIsLogin(true);
            setProfileHydratedOk(true);

            // 更新通参user_id
            registerTrackerParams({
                user_id: res.data.user_id,
            });

            syncArmsConfig();

            return true;
        } catch {
            return false;
        } finally {
            setProfileHydrationInflight(null);
        }
    })();

    setProfileHydrationInflight(promise);
    return promise;
}
